import React, { useMemo, useState, useEffect } from 'react';
import { Users, Search, ArrowUpDown, Loader2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReferredUsers, clearReferredUsers } from 'slices/referredUsers/referredUsersSlice';
import { RootState } from 'slices';

const ReferredUsers: React.FC = () => {
  const dispatch = useDispatch<any>();
  const { data, loading, error, count, hasMore } = useSelector((state: RootState) => state.ReferredUsers);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch data when component mounts
  useEffect(() => {
    dispatch(fetchReferredUsers({ page: 1, page_size: 25 }));
    
    // Cleanup on unmount
    return () => {
      dispatch(clearReferredUsers());
    };
  }, [dispatch]);

  const filtered = useMemo(() => {
    if (!searchTerm) return data;
    const q = searchTerm.toLowerCase();
    return data.filter((r) =>
      r.agent_name.toLowerCase().includes(q) ||
      (r.subagent_name ? r.subagent_name.toLowerCase().includes(q) : false) ||
      r.user_name.toLowerCase().includes(q)
    );
  }, [data, searchTerm]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Referred Users</h1>
            {count > 0 && (
              <p className="text-sm text-gray-600 mt-1">Total: {count} users</p>
            )}
          </div>
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by agent, sub agent, or user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-72"
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>ID</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Agent Name</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Sub Agent Name</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>User Name</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">
                    <div className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading referred users...
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-6 text-center text-sm text-gray-500">
                    <div className="inline-flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {searchTerm ? 'No referred users found matching your search.' : 'No referred users found.'}
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((row, idx) => (
                  <tr key={row.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.agent_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.subagent_name ?? '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.user_name}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReferredUsers;


